import { Remarkable } from 'remarkable'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _isFunction from 'lodash/isFunction'
import _isString from 'lodash/isString'
import _replace from 'lodash/replace'
import _trim from 'lodash/trim'
import _forEach from 'lodash/forEach'
import _toPairs from 'lodash/toPairs'
import katex from 'katex'
import { isProduction } from 'environment'
import { get as getMdConfig } from './config'

export function parse (options) {
  const {
    assets,
    alterMarkdownRender,
    alterTokenRender,
    mdConfig = getMdConfig(),
    tokenPattern = /(\[\[[\w]+:[\s]?[\w]+\]\])/g,
  } = options
  let { template } = options
  if (!_isString(template)) {
    if (!process.env.NODE_ENV || !isProduction()) {
      throw new Error('The template option is required to parse markdown.')
    } else {
      template = ''
    }
  }
  const mdParser = new Remarkable(mdConfig)

  const tmplArr = template.split(tokenPattern).filter((str) => str !== '')

  return _map(
    tmplArr,
    mapTokens({
      assets,
      tokenPattern,
      mdParser,
      alterMarkdownRender,
      alterTokenRender,
    }),
  )
}

export function mapTokens (options) {
  const {
    assets,
    tokenPattern,
    mdParser,
    alterMarkdownRender,
    alterTokenRender,
  } = options
  return (elem, index) => {
    // for valid tokens, get asset data and render embedded content
    if (tokenPattern.test(elem)) {
      const tokenKey = elem.substring(2, elem.length - 2)
      const tokenData = _get(assets, tokenKey, null)
      if (_isFunction(alterTokenRender)) {
        return alterTokenRender({ index, tokenKey, tokenData })
      }
      if (!tokenData) {
        return null
      }
      return null
    } if (/(\[\[[\s\S]*\]\])/g.test(elem)) {
      // for invalid tokens, avoid rendering token text to page
      return null
    }

    let html = mdParser.render(elem)
    if (_isFunction(alterMarkdownRender)) {
      html = alterMarkdownRender({ index, html })
    }
    return html
  }
}

export function anchorTextToHtml (str = '') {
  const anchorPattern = /&lt;a name=&quot;([\w-]+)&quot;&gt;&lt;\/a&gt;/g
  return str.replace(anchorPattern, '<a name="$1"></a>')
}

export function markdownToHtml (markdown) {
  if (!markdown) {
    return null
  }

  return parse({ template: markdown })
}

export function oracleMarkdownToHtml ({ markdown, sameTab = false }) {
  if (!markdown) {
    return null
  }

  // Extract LaTeX expressions first
  const { processedMarkdown, latexDict } = extractLatexExpressions(markdown)

  return parse({
    template: processedMarkdown,
    alterMarkdownRender: ({ html }) => {
      let processedHtml = html
      if (!sameTab) {
        // Replace all link tags to add target="_blank" and rel="noopener noreferrer"
        processedHtml = processedHtml.replace(
          /<a href="([^"]*)"([^>]*)>/g,
          '<a href="$1"$2 target="_blank" rel="noopener noreferrer">',
        )
      }

      // Inject rendered LaTeX
      processedHtml = renderLatexDict(processedHtml, latexDict)

      return processedHtml
    },
  })
}

/** This parses input markdown and identifies passages of LaTeX and puts them in
 * a lookup table for later use, and replaces those passages with a placeholder
 * that can be used for replacement by proper katex-generated HTML later. */
function extractLatexExpressions (markdown) {
  const latexDict = {}
  let inlineCounter = 0
  let displayCounter = 0

  // Extract inline LaTeX \( ... \)
  let processedMarkdown = _replace(markdown, /\\{1,2}\((.*?)\\{1,2}\)/gs, (match, content) => {
    const key = `INLINE_${inlineCounter}`
    inlineCounter += 1
    latexDict[key] = _trim(content)
    return `ĦĦ${key}ĦĦ`
  })

  // Extract display LaTeX \[ ... \]
  processedMarkdown = _replace(processedMarkdown, /\\{1,2}\[(.*?)\\{1,2}\]/gs, (match, content) => {
    const key = `DISPLAY_${displayCounter}`
    displayCounter += 1
    latexDict[key] = _trim(content)
    return `ĦĦ${key}ĦĦ`
  })

  return { processedMarkdown, latexDict }
}

/** This parses the given HTML and looks for the placeholders and replaces it
 * with the proper dom tree that users see. */
function renderLatexDict (html, latexDict) {
  let finalHtml = html

  _forEach(_toPairs(latexDict), ([key, texExpression]) => {
    const placeholder = `ĦĦ${key}ĦĦ`
    const isDisplayMode = key.startsWith('DISPLAY_')

    try {
      // Using official KaTeX.renderToString API
      const renderedLatex = katex.renderToString(texExpression, {
        displayMode: isDisplayMode,
        throwOnError: false, // Renders invalid input as red source
      })
      finalHtml = _replace(finalHtml, placeholder, renderedLatex)
    } catch (error) {
      // Fallback for severe errors
      const fallback = isDisplayMode
        ? `\\[${texExpression}\\]`
        : `\\(${texExpression}\\)`
      finalHtml = _replace(finalHtml, placeholder, fallback)
      console.warn(`LaTeX rendering failed for ${key}:`, error) // eslint-disable-line no-console
    }
  })

  return finalHtml
}

// Re-export markdown stripping utilities for TTS
export { stripMarkdownForTTS, stripMarkdownBasic } from './stripMarkdown'

// Export for testing
export { extractLatexExpressions }
